<template>
  <div class="carousel-container">
    <!-- 跑马灯外层 -->
    <div class="carousel-wrapper">
      <!-- 左按钮 -->
      <button class="carousel-btn left" @click="prev" :disabled="isAnimating">
        <img :src="leftIcon" alt="prev">
      </button>

      <!-- 轮播可视窗口 (全宽，隐藏溢出) -->
      <div class="carousel-viewport" ref="viewport">
        <!-- 轮播列表轨道 -->
        <div
          class="carousel-track"
          ref="carouselTrack"
          :style="{ transform: trackStyle }"
        >
          <div
            class="carousel-item"
            v-for="(item, index) in carouselList"
            :key="index"
            :style="{ width: itemWidthStr, marginRight: gapStr }"
          >
            <!-- 图片容器，保持比例 -->
            <div class="img-wrapper">
              <img :src="item.img" :alt="item.txt" />
              <div class="txt-wrapper">
                <!-- 左下角文字 -->
                <p class="title" v-if="item?.title" v-html="item.title"></p>
                <p class="desc" v-if="item?.desc" v-html="item.desc"></p>
              </div>
            </div>

            <!-- 左下角文字 -->
            <!-- <div class="carousel-txt" v-if="item?.txt">{{ item.txt }}</div> -->
          </div>
        </div>

        <!-- 固定指示器 (右下角) -->
        <div class="carousel-dots">
          <span
            v-for="(_, index) in carouselList"
            :key="index"
            class="dot"
            :class="{ active: current === index }"
            @click="goTo(index)"
          ></span>
        </div>
      </div>

      <!-- 右按钮 -->
      <button class="carousel-btn right" @click="next" :disabled="isAnimating">
        <img :src="rightIcon" alt="next">
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CarBannerCarousel',
  props: {
    carouselList: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      leftIcon: '/assets/images/icon-carousel-left-btn.png',
      rightIcon: '/assets/images/icon-carousel-right-btn.png',
      current: 0,
      isAnimating: false,
      autoPlayTimer: null,
      // 配置项：基于设计稿 1920px 宽，图片 1600px，间距 40px
      baseDesignWidth: 1920,
      baseItemWidth: 1600,
      baseGap: 40,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
    };
  },
  computed: {
    // 动态计算当前窗口下的图片宽度 (vw)
    // 保持原比例：1600 / 1920 ≈ 83.33vw
    itemWidthVw() {
      return (this.baseItemWidth / this.baseDesignWidth) * 100;
    },
    // 动态计算当前窗口下的间距 (vw)
    // 保持原比例：40 / 1920 ≈ 2.08vw
    gapVw() {
      return (this.baseGap / this.baseDesignWidth) * 100;
    },
    
    // 用于绑定的样式字符串
    itemWidthStr() {
      return `${this.itemWidthVw}vw`;
    },
    gapStr() {
      return `${this.gapVw}vw`;
    },

    // 核心：计算位移
    trackStyle() {
      const viewportWidth = this.windowWidth;
      
      // 单个单元占用的总宽度 (图片宽 + 间距) 的像素值
      // 注意：vw 转 px 计算
      const itemWidthPx = (this.itemWidthVw / 100) * viewportWidth;
      const gapPx = (this.gapVw / 100) * viewportWidth;
      
      // 当前图片中心点距离轨道起点的距离
      const currentCenter = (itemWidthPx + gapPx) * this.current + itemWidthPx / 2;
      
      // 视口中心点
      const viewportCenter = viewportWidth / 2;
      
      // 计算 translateX
      const moveX = viewportCenter - currentCenter;
      
      return `translateX(${moveX}px)`;
    }
  },
  mounted() {
    this.startAutoPlay();
    // 监听窗口大小变化，重新计算位置
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.stopAutoPlay();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    goTo(index) {
      if (this.isAnimating || this.current === index) return;
      this.isAnimating = true;
      this.current = index;
      this.resetAutoPlay();
      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    },

    prev() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.current = (this.current - 1 + this.carouselList.length) % this.carouselList.length;
      this.resetAutoPlay();
      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    },

    next() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.current = (this.current + 1) % this.carouselList.length;
      this.resetAutoPlay();
      setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    },

    startAutoPlay() {
      this.stopAutoPlay();
      this.autoPlayTimer = setInterval(() => {
        this.next();
      }, 5000); // 恢复为5秒
    },

    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    },

    resetAutoPlay() {
      this.stopAutoPlay();
      this.startAutoPlay();
    },
  },
};
</script>

<style lang="less" scoped>
/* 最外层容器 */
.carousel-container {
  width: 100%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  padding: 6.25vw 0;
  .carousel-wrapper {
    position: relative;
    width: 100%;
  }

  .carousel-viewport {
    width: 100%;
    overflow: hidden;
    position: relative;
  }

  /* 轨道 */
  .carousel-track {
    display: flex;
    transition: transform 0.5s ease-in-out;
    will-change: transform;
  }

  /* 单张卡片 */
  .carousel-item {
    flex-shrink: 0;
    position: relative;
    height: auto; 
    overflow: hidden;
  }

  /* 图片包裹层：用于控制宽高比 */
  .img-wrapper {
    width: 100%;
    /* 原始比例 1600x770 ≈ 2.758 */
    aspect-ratio: 1600 / 770; 
    overflow: hidden;
    position: relative;
    .txt-wrapper {
      position: absolute;
      bottom: 2vw;
      left: 3vw;
      color: #fff;
      font-weight: @fw2;
      .title {
        font-weight: @fw1;
        font-size: clamp(18px, 1.46vw, 28px);
      }
      .desc {
        font-size: clamp(16px, 1.25vw, 24px);
      }
    }
  }

  .img-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* 左下角文字 */
  .carousel-txt {
    position: absolute;
    left: 2vw; /* 使用相对单位 */
    bottom: 1vw;
    color: #fff;
    font-size: clamp(18px, 1.5vw, 28px);
    font-weight: @fw2;
    z-index: 2;
    white-space: nowrap;
  }

  /* 指示器 */
  .carousel-dots {
    position: absolute;
    right: 10vw;
    bottom: 1.5vw;
    z-index: 10;
    display: flex;
    gap: 10px;
  }

  .dot {
    width: 32px;
    height: 4px;
    background-color: rgba(174, 174, 174, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .dot:hover {
    background-color: rgba(174, 174, 174, 0.7);
  }

  .dot.active {
    width: 30px;
    background-color: rgba(174, 174, 174, 1);
    opacity: 1;
  }

  /* 左右按钮 */
  .carousel-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: clamp(40px, 3vw, 64px); /* 响应式按钮大小 */
    height: clamp(50px, 3vw, 64px);
    border-radius: 50%;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* 按钮位置：在可视区域边缘内侧 */
  .carousel-btn.left {
    left: 11vw; 
  }

  .carousel-btn.right {
    right: 10vw;
  }
}



/* 移动端适配 */
/* @media (max-width: 768px) {
  .carousel-txt {
    font-size: 14px;
    left: 15px;
    bottom: 15px;
  }
  
  .carousel-dots {
    right: 15px;
    bottom: 15px;
  }
  
} */
</style>